import { create } from 'zustand';
import type { CampaignNpc } from '@machina/core';
import { trpc } from '../lib/trpc';
import { createSubscription } from '../lib/realtimeHelpers';

/** Result from AI NPC extraction */
export interface NpcExtractionResult {
  npcs: Array<{
    name: string;
    title?: string;
    description: string;
    disposition?: string;
    dialogueHints: string[];
    role?: 'leader' | 'lieutenant' | 'agent' | 'sympathizer';
    isKeystone: boolean;
    gmNotes: string;
  }>;
  sourceNote: string;
}

/**
 * NPC Store
 *
 * Manages campaign-scoped NPCs with Supabase realtime sync.
 * Follows the factionStore pattern.
 */

interface NpcState {
  npcs: CampaignNpc[];
  isLoading: boolean;
  error: string | null;
  realtimeCleanup: (() => void) | null;

  // Actions
  loadNpcs: (campaignId: string) => Promise<void>;
  createNpc: (campaignId: string, data: Partial<CampaignNpc>) => Promise<CampaignNpc | null>;
  updateNpc: (npcId: string, updates: Partial<CampaignNpc>) => Promise<void>;
  toggleKeystone: (npcId: string) => Promise<void>;
  deleteNpc: (npcId: string) => Promise<void>;
  seedFromFactions: (campaignId: string) => Promise<number>;
  extractFromText: (content: string, context?: string) => Promise<NpcExtractionResult>;
  fetchUrl: (url: string) => Promise<{ content: string; title?: string }>;
  importNpcs: (campaignId: string, npcs: Partial<CampaignNpc>[]) => Promise<CampaignNpc[]>;

  // Realtime
  subscribeToRealtime: (campaignId: string) => void;
  unsubscribeFromRealtime: () => void;

  // Queries
  getKeystones: () => CampaignNpc[];
  getNpcsByFaction: (factionId: string) => CampaignNpc[];

  // NPC speaker (shared state so any component can trigger "speak as NPC")
  speakingAs: CampaignNpc | null;
  setSpeakingAs: (npc: CampaignNpc | null) => void;
}

export const useNpcStore = create<NpcState>((set, get) => ({
  npcs: [],
  isLoading: false,
  error: null,
  realtimeCleanup: null,

  // ==========================================================================
  // LOAD
  // ==========================================================================

  loadNpcs: async (campaignId: string) => {
    set({ isLoading: true, error: null });
    try {
      const npcs = await trpc.npcs.list.query({ campaignId });
      set({ npcs, isLoading: false });
    } catch (err) {
      console.error('[NpcStore] Error loading NPCs:', err);
      set({ error: 'Failed to load NPCs', isLoading: false });
    }
  },

  // ==========================================================================
  // MUTATIONS
  // ==========================================================================

  createNpc: async (campaignId: string, data: Partial<CampaignNpc>) => {
    try {
      const npc = await trpc.npcs.create.mutate({
        campaignId,
        name: data.name || 'New NPC',
        title: data.title,
        description: data.description || '',
        factionId: data.factionId,
        districtId: data.districtId,
        role: data.role,
        isKeystone: data.isKeystone ?? true,
        status: data.status || 'active',
        disposition: data.disposition,
        dialogueHints: data.dialogueHints || [],
        gmNotes: data.gmNotes || '',
        isSecret: data.isSecret || false,
      });

      set((state) => ({ npcs: [...state.npcs, npc] }));
      return npc;
    } catch (err) {
      console.error('[NpcStore] Error creating NPC:', err);
      set({ error: 'Failed to create NPC' });
      return null;
    }
  },

  updateNpc: async (npcId: string, updates: Partial<CampaignNpc>) => {
    try {
      const result = await trpc.npcs.update.mutate({
        id: npcId,
        name: updates.name,
        title: updates.title,
        description: updates.description,
        factionId: updates.factionId,
        role: updates.role,
        districtId: updates.districtId,
        currentLocation: updates.currentLocation,
        isKeystone: updates.isKeystone,
        status: updates.status,
        disposition: updates.disposition,
        dialogueHints: updates.dialogueHints,
        gmNotes: updates.gmNotes,
        isSecret: updates.isSecret,
      });

      set((state) => ({
        npcs: state.npcs.map((n) => (n.id === npcId ? result : n)),
      }));
    } catch (err) {
      console.error('[NpcStore] Error updating NPC:', err);
      set({ error: 'Failed to update NPC' });
    }
  },

  toggleKeystone: async (npcId: string) => {
    try {
      const result = await trpc.npcs.toggleKeystone.mutate({ id: npcId });

      set((state) => ({
        npcs: state.npcs.map((n) => (n.id === npcId ? result : n)),
      }));
    } catch (err) {
      console.error('[NpcStore] Error toggling keystone:', err);
      set({ error: 'Failed to toggle keystone' });
    }
  },

  deleteNpc: async (npcId: string) => {
    try {
      await trpc.npcs.delete.mutate({ id: npcId });

      set((state) => ({
        npcs: state.npcs.filter((n) => n.id !== npcId),
      }));
    } catch (err) {
      console.error('[NpcStore] Error deleting NPC:', err);
      set({ error: 'Failed to delete NPC' });
    }
  },

  seedFromFactions: async (campaignId: string) => {
    set({ isLoading: true });
    try {
      const result = await trpc.npcs.seedFromFactions.mutate({ campaignId });
      // Reload full list after seeding
      await get().loadNpcs(campaignId);
      return result.seeded;
    } catch (err) {
      console.error('[NpcStore] Error seeding NPCs:', err);
      set({ error: 'Failed to seed NPCs', isLoading: false });
      return 0;
    }
  },

  extractFromText: async (content: string, context?: string) => {
    return trpc.npcs.extractFromText.mutate({ content, context });
  },

  fetchUrl: async (url: string) => {
    return trpc.npcs.fetchUrl.mutate({ url });
  },

  importNpcs: async (campaignId: string, npcs: Partial<CampaignNpc>[]) => {
    const created: CampaignNpc[] = [];
    for (const npc of npcs) {
      const result = await get().createNpc(campaignId, npc);
      if (result) created.push(result);
    }
    return created;
  },

  // ==========================================================================
  // REALTIME
  // ==========================================================================

  subscribeToRealtime: (campaignId: string) => {
    get().unsubscribeFromRealtime();

    const { cleanup } = createSubscription({
      channelName: `npcs-${campaignId}`,
      table: 'machina_campaign_npcs',
      filter: `campaign_id=eq.${campaignId}`,
      event: '*',
      onPayload: () => {
        get().loadNpcs(campaignId);
      },
    });

    set({ realtimeCleanup: cleanup });
  },

  unsubscribeFromRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },

  // ==========================================================================
  // QUERIES
  // ==========================================================================

  getKeystones: () => {
    return get().npcs.filter((n) => n.isKeystone);
  },

  getNpcsByFaction: (factionId: string) => {
    return get().npcs.filter((n) => n.factionId === factionId);
  },

  speakingAs: null,
  setSpeakingAs: (npc) => set({ speakingAs: npc }),
}));
